import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { LOADING } from 'store';
import { Link, useHistory } from 'react-router-dom';
import { Row, Col, Card, Button, Table, Modal } from 'react-bootstrap';
import { parseDateBR } from 'helpers/date';
import { useTranslation } from 'react-i18next';

import { api } from 'services/api';
import ListingStatuses from 'components/ListingStatuses';
import Can from 'components/Can';
import Status from 'enums/ActionStatus.js'

const MarketingPlanFormActions = ({
  planId,
  status,
  companyId,
  companyRuleId,
  setActions,
  actions,
  loading,
  component,
  hash,
}) => {
  const history = useHistory();
  const defaultFilters = {
    marketing_plan_id: planId,
    per_page: 10,
    page: 1,
  };

  const callBackLoading = useCallback(loading, []);

  const [modalShow, setModalShow] = useState(false);
  const [existingActions, setExistingActions] = useState([]);
  const [marketing, setMarketing] = useState([]);
  const [steps, setSteps] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);

  useEffect(() => {
    api
      .get('flows/1/actions-count', { params: { marketing_plan_id: planId } })
      .then(({ data }) => {
        setSteps(data.steps);

          setFilters({
            ...{
              marketing_plan_id: planId,
              per_page: 10,
              page: 1,
            },
          });
        });
  }, [planId]);

  useEffect(() => {
    api
      .get('actions', { params: filters })
      .then(({ data }) => setActions(data));
    callBackLoading(false);
  }, [filters, setActions, callBackLoading]);

  useEffect(() => {
    api.get(`marketing-plans/${planId}`).then(({ data }) => {
      setMarketing(data);
      status = data.status_id;
    });
  }, []);

  const handleFilterChange = (new_filters, reset = false) => {
    if(new_filters.search){
      new_filters.__search = new_filters.search;
    }
    delete new_filters.search;
    let f = { ...filters, ...new_filters };

    if (reset) f = { ...defaultFilters, ...new_filters };
    setFilters(f);
  };

  const handleModalShow = async () => {
    let aux_filter = {}
    
    if ([Status.Approved, Status.CustomerApproval].includes(status)) {
      // 21,13,14,17,18
      aux_filter['status_id'] = "21,13,14,17,18";
    }

    await api
      .get(`actions/${companyId}/${companyRuleId}`, { params: { ...aux_filter } })
      .then(({ data }) => setExistingActions(data));

    setModalShow(true);
  };

  const getFlowActionsCount = () => {
    callBackLoading(true);
    api
      .get('flows/1/actions-count', { params: { marketing_plan_id: planId } })
      .then(({ data }) => {
        setSteps(data.steps);

        setFilters({
          ...defaultFilters,
          ...{ status_id: null },
        });
      });
  };

  const handleAddPlan = (id) => {
    setModalShow(false);

    api
      .post('actions/add-plan', { action_id: id, plan_id: planId })
      .then(() => getFlowActionsCount());
  };

  const unlinkPlan = (id) => {
    api
      .post('actions/remove-plan', { action_id: id, marketing_plan_id: planId })
      .then(() => getFlowActionsCount());
  };

  const { t } = useTranslation();

  const getLocale = (currency) => {
    switch (currency.toString()) {
      case '1':
        return 'BRL';
        break;
      case '2':
        return 'USD';
        break;
      case '3':
        return 'EUR';
        break;
      default:
        break;
    }
  };


  return (
    <>
      <Modal
        show={modalShow} 
        dialogClassName="modal-90w"
        onHide={() => setModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('label:select action')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th className="text-center align-middle">
                    {t('label:name')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:status')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:start date')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:end date')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:payment date')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:value')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:activity')}
                  </th>
                  <th className="text-center align-middle">
                    {t('label:actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {existingActions.map((action, k) => {
                  var value = 0;

                  action.all_values.forEach(
                    (action) => (value += action.actual_value)
                  );

                  if (!!action.value_extra)
                    value += action.value_extra.actual_value;

                  return (
                    <tr key={k}>
                      <td className="text-center align-middle">
                        {action.name}
                      </td>
                      <td className="text-center align-middle">
                        {t(`statuses:${action.status.name}`)}
                      </td>
                      <td className="text-center align-middle">
                        {action.start_date
                          ? parseDateBR(action.start_date)
                          : '--'}
                      </td>
                      <td className="text-center align-middle">
                        {action.end_date ? parseDateBR(action.end_date) : '--'}
                      </td>
                      <td className="text-center align-middle">
                        {actions.payment_date
                          ? parseDateBR(action.payment_date)
                          : '--'}
                      </td>
                      <td className="text-center align-middle">
                        {value.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: getLocale(action.currency),
                        })}
                      </td>
                      <td className="text-center align-middle">
                        {action.task
                          ? t(`task:${action.task.task_group.name}`)
                          : '--'}
                      </td>
                      <td className="text-center align-middle">
                        <Button
                          size="sm"
                          variant="light"
                          onClick={() => handleAddPlan(action.id)}
                        >
                          <i className="fas fa-plus" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          }
        </Modal.Body>
      </Modal>
      <Row className="mt-2 mb-2">
        <Col>
          {marketing.status_id === 20 || marketing.status_id === 8 ? (
            <>
              <Row>
                <Col>
                  <h4>{t('title:Landing Page')}</h4>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <Button
                    size="sm"
                    disabled={marketing.status_id !== 20}
                    as={Link}
                    to={{
                      pathname: `/marketing-plans/landing-approval/${hash}`,
                      query: {
                        status,
                        hash,
                        planId,
                        companyId,
                      },
                    }}
                    variant="dark"
                    target="_blank"
                  >
                    <i className="fas fa-plus"></i> {t('label:Landing Page')}
                  </Button>
                </Col>
              </Row>
            </>
          ) : null}

          <Row className="mt-4">
            <Col>
              <h4>{t('title:actions')}</h4>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <Can do="createAction" on="App">
              {![Status.Approved, Status.CustomerApproval].includes(status) ? 
                <Button
                  disabled={!component.editable}
                  as={!!component.editable ? Link : null}
                  to={{
                    pathname: '/actions/create',
                    query: {
                      planId,
                      companyId,
                      companyRuleId,
                    },
                    state:{
                      from:history.location.pathname
                    }
                  }}
                  size="sm"
                  variant="dark"
                >
                  <i className="fas fa-plus"></i> {t('label:add new')}
                </Button>
                : ('') }
              </Can>
              &nbsp;
              <Button
                onClick={handleModalShow}
                disabled={!component.editable}
                size="sm"
                variant="dark"
              >
                <i className="fas fa-plus"></i> {t('label:select action')}
              </Button>
            </Col>
          </Row>

          {actions.length !== 0 && (
            <>
              <Row className="mt-4">
                <Col>
                  <h4>{t('title:registered actions')}</h4>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <ListingStatuses
                    statuses={steps.map((step) => ({
                      ...step.status,
                      ...{ count: step.actions_count },
                    }))}
                    fields={[
                      { name: '#', key: 'id', type: 'string' },
                      { name: 'name', key: 'name', type: 'string' },
                      { name: 'start date', key: 'start_date', type: 'date' },
                      { name: 'end date', key: 'end_date', type: 'date' },
                      {
                        name: 'payment date',
                        key: 'payment_date',
                        type: 'date',
                      },
                      {
                        name: 'value',
                        type: 'custom',
                        value: (_, data) => {
                          let value = parseFloat(data.total_value);
                          // data.all_values.forEach(
                          //   (action) => (value += action.actual_value)
                          // );

                          return value.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: getLocale(data.currency),
                          });
                        },
                      },
                      {
                        name: 'activity',
                        key: 'task_task_group_name',
                        type: 'string',
                      },
                    ]}
                    data={actions}
                    handleFilterChange={handleFilterChange}
                    filters={[]}
                    stateFilters={filters}
                    setFilters={setFilters}
                    actions={[
                      (action, k) => (
                        <Can do="editAction" on="App">
                          {' '}
                          <Button
                            size="sm"
                            variant="light"
                            key={k}
                            as={Link}
                            to={{
                              pathname:`/actions/edit/${action.id}`,
                              state:{
                                from:history.location.pathname
                              }
                            }}
                          >
                            <i className="fas fa-pen" />
                          </Button>
                        </Can>
                      ),
                      (action, k) => (
                        <Button
                          size="sm"
                          key={k}
                          variant="danger"
                          className="ml-1"
                          onClick={() => unlinkPlan(action.id)}
                        >
                          <i className="fas fa-unlink" />
                        </Button>
                      ),
                    ]}
                  />
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  loading: (payload) => dispatch({ type: LOADING, payload }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MarketingPlanFormActions);
